import React from "react";
import copyToClipboard from "copy-to-clipboard";

import { AppDescription, AppKind, getAppKind } from "common/components/SerializedApp";
import { DataStore } from "common/components/types";
import { makeSimpleAppDescriptionContext } from "common/generator/components";
import { TypeSchema } from "common/generator/description";
import { WireBackendImpl } from "webapp/hydrated-ui/backend";
import { useChangeObservable } from "hooks/use-change-observable";
import { WireNavigationModel } from "common/wire";
import { useCleanupMemo } from "./use-cleanup-memo";
import { BuilderActionsForApp } from "common/Database/builder-actions";
import { localStorageGetItem, localStorageSetItem } from "webapp/lib/local-storage";
import { getAppFacilities } from "webapp/lib/app-renderer";
import AudioPlayer from "webapp/lib/audio";

interface WireBackendResult {
    backend?: WireBackendImpl;
    navModel?: WireNavigationModel;
    onBack: () => void;
}

export function useWireBackend(
    appID: string,
    appDescription: AppDescription | undefined,
    builderActions: BuilderActionsForApp | undefined,
    schema: TypeSchema | undefined,
    dataStore: DataStore | undefined,
    isBuilder: boolean,
    startPath?: string
): WireBackendResult {
    const urlStack = React.useRef<string[]>(startPath === undefined ? [] : [startPath]);

    const backendRef = React.useRef<WireBackendImpl>();
    const onBack = React.useCallback(() => {
        urlStack.current.pop();
        backendRef.current?.urlPathChanged(urlStack.current.slice(-1)[0] ?? "");
    }, []);

    const backend = useCleanupMemo(() => {
        if (
            appDescription === undefined ||
            getAppKind(appDescription) !== AppKind.Page ||
            schema === undefined ||
            dataStore === undefined
        ) {
            return [undefined, () => undefined];
        }
        const adc = makeSimpleAppDescriptionContext(appID, appDescription, builderActions, schema);
        const result = new WireBackendImpl(
            appID,
            getAppFacilities(),
            dataStore,
            adc,
            {
                openLink: url => window.open(url),
                showToast: (success: boolean, message: string) => {
                    alert(`${success ? "Success!" : "Error!"}\n${message}`);
                },
                copyToClipboard: data => copyToClipboard(data),
                playSound: sound => {
                    const player = new AudioPlayer(sound);
                    player.play();
                },
                // FIXME: Implement
                signIn: () => undefined,
                loadStateValues: key => {
                    try {
                        const s = localStorageGetItem(`state-${key}`);
                        if (s === undefined) return {};
                        return JSON.parse(s);
                    } catch {
                        return {};
                    }
                },
                saveStateValues: (key, values) => localStorageSetItem(`state-${key}`, JSON.stringify(values)),
            },
            isBuilder,
            backendRef.current ?? urlStack.current.slice(-1)[0]
        );
        return [result, () => result.retire];
    }, [appDescription, appID, builderActions, dataStore, isBuilder, schema]);

    backendRef.current = backend;

    const navModel = useChangeObservable(backend?.wireNavigationModelObservable);
    const urlPath = navModel?.urlPath ?? "";
    React.useEffect(() => {
        if (urlPath !== urlStack.current.slice(-1)[0]) {
            urlStack.current.push(urlPath);
        }
    }, [urlPath]);

    return { backend, navModel, onBack };
}

export const WireBackendContext = React.createContext<WireBackendResult>({ onBack: () => undefined });

export function useWireBackendContext(): WireBackendResult {
    return React.useContext(WireBackendContext);
}
